<!--
Copyright (C) 2025 Checkmk GmbH - License: GNU General Public License v2
This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
conditions defined in the file COPYING, which is part of this source code package.
-->
<script setup lang="ts">
import { computed, ref } from 'vue'

import usei18n from '@/lib/i18n'
import type { TranslatedString } from '@/lib/i18nString'

import CmkAlertBox from '@/components/CmkAlertBox.vue'
import CmkCode from '@/components/CmkCode.vue'
import CmkCollapsible from '@/components/CmkCollapsible'
import CmkCollapsibleTitle from '@/components/CmkCollapsible/CmkCollapsibleTitle.vue'
import CmkIndent from '@/components/CmkIndent.vue'
import CmkToggleButtonGroup from '@/components/CmkToggleButtonGroup.vue'
import { CmkWizardButton } from '@/components/CmkWizard'
import CmkWizardStep from '@/components/CmkWizard/CmkWizardStep.vue'
import { getWizardContext } from '@/components/CmkWizard/utils.ts'
import CmkHeading from '@/components/typography/CmkHeading.vue'
import CmkParagraph from '@/components/typography/CmkParagraph.vue'

import type { AgentSlideOutTabs } from '../../lib/type_def'
import GenerateToken from '../GenerateToken.vue'

const { _t } = usei18n()

const props = defineProps<{
  index: number
  isCompleted: () => boolean
  tab: AgentSlideOutTabs
  isPushMode: boolean
  closeButtonTitle: TranslatedString
  hostName: string
  siteId: string
  userSettingsUrl: string
  agentReceiverPortIsDefault: boolean
}>()

const selectedVariantId = defineModel<string>('selectedVariantId', { default: '' })
const emit = defineEmits(['close'])
const context = getWizardContext()
const ott = ref<string | null | Error>(null)
const collapsibleOpen = ref<boolean>(false)

const activeRegistrationCmd = computed<string | undefined>(() => {
  const variants = props.tab.registrationCmdVariants
  if (variants && variants.length > 0) {
    return variants.find((v) => v.id === selectedVariantId.value)?.cmd ?? variants[0]!.cmd
  }
  return props.tab.registrationCmd
})

const regAgentOttCmd = computed(() => {
  const cmd = activeRegistrationCmd.value
  if (!cmd) {
    return ''
  }
  if (ott.value && !(ott.value instanceof Error)) {
    return cmd.replace('--user agent_registration', `--ott 0:${ott.value}`)
  }
  return cmd
})

function reset() {
  ott.value = null
}
</script>
<template>
  <CmkWizardStep :index="index" :is-completed="isCompleted">
    <template #header>
      <CmkHeading> {{ _t('Register agent') }}</CmkHeading>
    </template>
    <template #content>
      <div v-if="context.isSelected(index)">
        <div v-if="tab.registrationMsg && (tab.registrationCmd || tab.registrationCmdVariants)">
          <div class="register-heading-row">
            <CmkParagraph>
              {{
                _t(
                  `Agent registration will establish trust between the Agent Controller
                    on the host and the Agent Receiver on the Checkmk server.`
                )
              }}
            </CmkParagraph>
          </div>

          <GenerateToken
            v-model="ott"
            token-generation-endpoint-uri="domain-types/agent_registration_token/collections/all"
            :expires-in-seconds="604800"
            :token-generation-body="{
              host: hostName,
              comment: 'Agent registration token for agent slideout',
              site_id: siteId
            }"
            :description="_t('This requires the generation of a registration token.')"
          />
          <template v-if="ott !== null">
            <CmkToggleButtonGroup
              v-if="tab.registrationCmdVariants && tab.registrationCmdVariants.length > 1"
              v-model="selectedVariantId"
              class="mh-register-agent__shell-toggle"
              :options="tab.registrationCmdVariants.map((v) => ({ label: v.label, value: v.id }))"
            />
            <CmkParagraph>{{ tab.registrationMsg }}</CmkParagraph>
            <CmkCode :code_txt="regAgentOttCmd" class="code" width="fill" />
            <CmkAlertBox v-if="agentReceiverPortIsDefault" variant="warning" size="small">
              {{
                _t(
                  'The agent receiver port could not be determined from the remote site. The command uses the default port (8000). Adjust the --server port if your site uses a different agent receiver port.'
                )
              }}
            </CmkAlertBox>
          </template>
        </div>
      </div>
      <div v-else>
        <CmkParagraph>
          {{ _t('Run this command to register the Checkmk agent controller.') }}
        </CmkParagraph>
      </div>

      <CmkCollapsibleTitle
        :open="collapsibleOpen"
        :title="_t('Troubleshooting registration issues: Authenticate with the registration user')"
        @toggle-open="collapsibleOpen = !collapsibleOpen"
      />
      <CmkCollapsible :open="collapsibleOpen">
        <CmkIndent>
          <CmkParagraph>
            {{
              _t(`Registration fails if the token cannot be authorized. In this case,
              authenticate using the`)
            }}
            <b>{{ _t('agent_registration') }}</b>
            {{ _t(`user instead of the token.`) }}
          </CmkParagraph>
          <br />
          <CmkParagraph>
            {{
              _t(
                `When you run the command in the terminal, you will be prompted for the password of the
                agent_registration user. Copy the 'Automation secret for machine accounts' from the `
              )
            }}
            <a :href="userSettingsUrl" target="_blank"> {{ _t('agent_registration user') }}</a>
            {{ _t(`and paste it into the terminal to continue the registration.`) }}
          </CmkParagraph>
          <CmkToggleButtonGroup
            v-if="tab.registrationCmdVariants && tab.registrationCmdVariants.length > 1"
            v-model="selectedVariantId"
            class="mh-register-agent__shell-toggle"
            :options="tab.registrationCmdVariants.map((v) => ({ label: v.label, value: v.id }))"
          />
          <CmkCode :code_txt="activeRegistrationCmd ?? ''" class="code" width="fill" />
        </CmkIndent>
      </CmkCollapsible>
    </template>
    <template v-if="context.isSelected(index)" #actions>
      <CmkWizardButton
        v-if="!isPushMode"
        type="finish"
        :override-label="closeButtonTitle"
        :disabled="ott === null"
        icon-name="connection-tests"
        @click="emit('close')"
      />
      <CmkWizardButton v-else type="next" :disabled="ott === null" />
      <CmkWizardButton type="previous" @click="reset" />
    </template>
  </CmkWizardStep>
</template>

<style scoped>
.mh-register-agent__panel {
  max-width: 650px;
}

.mh-register-agent__shell-toggle {
  margin-top: var(--dimension-5);
  margin-bottom: var(--dimension-5);
}
</style>
